import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { Button } from 'semantic-ui-react'

interface Props {
  link: string
  label?: string
  copiedLabel?: string
  onTimeout?: (() => void) | null
  timeoutMs?: number
  disabled?: boolean
  basic?: boolean
  className?: string
}

const CopyToClipboardButton = ({
  link,
  label = 'copy link',
  copiedLabel = 'copied',
  onTimeout = null,
  timeoutMs = 2000,
  disabled = false,
  basic = false,
  className = '',
}: Props) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const copyToClipboard = async (value: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value)
      return
    }

    const textArea = document.createElement('textarea')
    textArea.value = value
    textArea.setAttribute('readonly', 'true')
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.top = '0'
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (disabled) {
      return
    }

    try {
      await copyToClipboard(link)
      setCopied(true)
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => {
        setCopied(false)
        if (onTimeout) {
          onTimeout()
        }
      }, timeoutMs)
    } catch {
      // Ignore clipboard failures to avoid blocking the UI.
    }
  }

  return (
    <Button
      basic={basic}
      disabled={disabled}
      onClick={handleClick}
      className={classnames('CopyToClipboardButton', className, {
        CopyToClipboardButton__copied: copied,
      })}
    >
      {copied ? copiedLabel : label}
    </Button>
  )
}

export default CopyToClipboardButton
